import { Injectable } from '@angular/core';
import {
    FlyFreelyError,
    GqlMissionCriteria,
    isApprovalApplicable,
    MissionService,
    MissionSortField,
    MissionSummary,
    WorkTracker
} from '@flyfreely-portal-ui/flyfreely';
import { BehaviorSubject, ReplaySubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

function flattenMissionSummary(m: MissionSummary) {
    return {
        ...m,
        locationName: m.location?.name,
        organisationName: m.organisation?.name,
        missionCrewDetails: m.missionCrew.map((p: any) => ({
            person: p.person,
            role: p.missionRole
        })),
        craftNicknames: m.rpaLinkList.map(c => c.rpa.nickname),
        missionType: m.missionOperationType,
        missionWorkflowVersion: m.workflowVersion?.workflow,
        primaryMissionApproval:
            m.workflowVersion?.delegatedAuthority?.id != null
                ? m.missionApprovals.find(
                      a =>
                          m.workflowVersion!.delegatedAuthority!.id ===
                              a.authority.id &&
                          isApprovalApplicable(a.status)
                  )
                : null,
        flightConformanceResultList: m.flightConformanceResultList
    };
}

/**
 * This class coordinates consumers, and talks to the relevant mission service.
 *
 * All of its APIs take mission service API objects, not UI objects. It also uses
 * API terminology, not UI specific terminology.
 *
 * The API it exposes is a limited form of the full search API that is suitable for
 * what the Mission View requires.
 */
@Injectable()
export class MissionViewDataService {
    private workingSource = new ReplaySubject<boolean>(1);
    private missionsSource = new ReplaySubject<
        ReturnType<typeof flattenMissionSummary>[]
    >(1);
    private calendarMissionsSource = new ReplaySubject<
        ReturnType<typeof flattenMissionSummary>[]
    >(1);
    private currentPageSource = new BehaviorSubject<number>(0);
    private totalItemsSource = new ReplaySubject<number>(1);
    private hasMissionsSource = new ReplaySubject<boolean>(1);

    /**
     * This is the current search conditions. This should only contain a full
     * set of parameters, which is guarded by `findMissions`
     */
    private searchCriteriaSubject = new ReplaySubject<{
        criteria: GqlMissionCriteria;
        page: number;
        pageSize: number;
        sorting: MissionSortField | null;
    }>(1);

    private calendarSearchCriteriaSubject = new ReplaySubject<{
        criteria: GqlMissionCriteria;
    }>(1);

    searchCriteria$ = this.searchCriteriaSubject.asObservable();
    calendarSearchCriteria$ = this.calendarSearchCriteriaSubject.asObservable();

    working$ = this.workingSource.asObservable();
    missions$ = this.missionsSource.asObservable();
    calendarMissions$ = this.calendarMissionsSource.asObservable();
    currentPage$ = this.currentPageSource.asObservable();
    totalItems$ = this.totalItemsSource.asObservable();
    hasMissions$ = this.hasMissionsSource.asObservable();

    private workTracker = new WorkTracker();
    private ngUnsubscribe$ = new Subject<void>();

    constructor(private missionService: MissionService) {
        this.workTracker.observable
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(working => this.workingSource.next(working));

        this.searchCriteria$.subscribe(criteria =>
            this.updateMissionTable(criteria)
        );

        this.calendarSearchCriteria$.subscribe(criteria =>
            this.updateCalendarMission(criteria)
        );
    }

    ngOnDestroy() {
        this.workingSource.complete();
        this.missionsSource.complete();
        this.currentPageSource.complete();
        this.totalItemsSource.complete();
        this.hasMissionsSource.complete();
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
        this.searchCriteriaSubject.complete();
    }

    /**
     * Perform a mission search. This only supports sorting on one column.
     *
     * @param criteria the criteria to search for
     * @param page the page to paginate to
     * @param pageSize the number of items per page
     * @param sorting the sort column, or null if there is no sorting
     */
    findMissions(
        criteria: GqlMissionCriteria,
        page: number,
        pageSize: number,
        sorting: MissionSortField | null
    ) {
        this.searchCriteriaSubject.next({
            criteria,
            page,
            pageSize,
            sorting
        });
    }

    findCalendarMissions(criteria: GqlMissionCriteria) {
        this.calendarSearchCriteriaSubject.next({ criteria });
    }

    /**
     * This is reponsible for normalising the sort fields, and
     * then passing through to the search method, managing working
     * state while it does it.
     */
    private updateMissionTable({
        page,
        pageSize,
        sorting,
        criteria
    }: {
        page: number;
        pageSize: number;
        sorting: MissionSortField | null;
        criteria: GqlMissionCriteria;
    }) {
        // doneWorking is used for GraphQL to prevent the working state ending early.
        const doneWorking = this.workTracker.createTracker();

        const sortFields = sorting != null ? [sorting] : [];
        // this.subscriptionTypes$ is used here to ensure it has a value and prevent a race condition.

        this.missionService
            .findMissionSummaries(criteria, {
                page,
                pageSize,
                sortFields
            })
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: missions => {
                    const missionDetails = missions.results.map(
                        flattenMissionSummary
                    );
                    this.totalItemsSource.next(missions.count);
                    this.currentPageSource.next(page);
                    this.missionsSource.next(missionDetails);
                    if (missions.count > 0) {
                        this.hasMissionsSource.next(true);
                    }
                    doneWorking();
                },
                error: (error: FlyFreelyError) => {
                    console.error(error);
                    this.missionsSource.next([]);
                    this.totalItemsSource.next(0);
                    this.currentPageSource.next(0);
                    doneWorking();
                }
            });
    }

    private updateCalendarMission({
        criteria
    }: {
        criteria: GqlMissionCriteria;
    }) {
        // doneWorking is used for GraphQL to prevent the working state ending early.
        const doneWorking = this.workTracker.createTracker();

        this.missionService
            .findMissionSummaries(criteria, {
                page: 0,
                pageSize: 1000
            })
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: missions => {
                    const missionDetails = missions.results.map(
                        flattenMissionSummary
                    );
                    this.calendarMissionsSource.next(missionDetails);
                    doneWorking();
                },
                error: (error: FlyFreelyError) => {
                    console.error(error);
                    this.calendarMissionsSource.next([]);
                    doneWorking();
                }
            });
    }

    /**
     * Check if there are any missions in the organisation if no filters are applied
     * This is used primarily for messaging in the mission list widget
     * @param organisationId the Organisation ID to check against
     */
    checkMissionTotals(
        organisationId: number,
        authorityIds?: number[],
        orOrganisationId?: number
    ) {
        // doneWorking is used for GraphQL to prevent the working state ending early.
        const doneWorking = this.workTracker.createTracker();
        return this.missionService
            .findMissionSummaries(
                {
                    organisationId,
                    authorityIds:
                        orOrganisationId != null ? authorityIds : undefined,
                    orOrganisationId
                },
                {
                    page: 0,
                    pageSize: 1,
                    sortFields: []
                }
            )
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: result => {
                    this.hasMissionsSource.next(result.count > 0);
                    doneWorking();
                },
                error: (error: FlyFreelyError) => {
                    console.error(error);
                    doneWorking();
                }
            });
    }
}
